/**
 * Resolve the active color mode (dark | light) from the Outlook theme, falling
 * back to the OS prefers-color-scheme when Office theme isn't available.
 *
 * Why luminance: Office.OfficeTheme exposes bodyBackgroundColor but `isDarkTheme`
 * is explicitly NOT supported in Outlook (see @types/office-js). So we derive
 * dark/light from the body background luminance.
 */
import { useEffect, useState } from "react";
import { getOfficeTheme, onOfficeThemeChanged } from "@/office/integration";

export type ColorMode = "dark" | "light";

/** Relative luminance of an #RRGGBB color, 0 (black) .. 1 (white). */
function luminance(hex: string): number {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return 1; // unknown → treat as light
  const r = parseInt(m[1].slice(0, 2), 16) / 255;
  const g = parseInt(m[1].slice(2, 4), 16) / 255;
  const b = parseInt(m[1].slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function resolveColorMode(): ColorMode {
  const t = getOfficeTheme();
  if (t?.bodyBackgroundColor) {
    return luminance(t.bodyBackgroundColor) < 0.5 ? "dark" : "light";
  }
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return "dark";
}

export function useOfficeTheme(): ColorMode {
  const [mode, setMode] = useState<ColorMode>(resolveColorMode);

  useEffect(() => {
    const update = () => setMode(resolveColorMode());

    // Outlook theme changes (Mailbox 1.14+). No-op when unsupported.
    const off = onOfficeThemeChanged(update);

    // OS fallback: react to system dark/light changes when Office theme
    // isn't driving the decision.
    const mq =
      typeof window !== "undefined" ? window.matchMedia?.("(prefers-color-scheme: dark)") : null;
    const mqHandler = () => {
      if (!getOfficeTheme()) update();
    };
    mq?.addEventListener?.("change", mqHandler);

    return () => {
      off();
      mq?.removeEventListener?.("change", mqHandler);
    };
  }, []);

  return mode;
}
